import { useQuery } from "@tanstack/react-query";
import { DollarSign, Zap } from "lucide-react";
import { rpcClient } from "@/lib/rpc";

/**
 * Cost dashboard for the engineering pillar. Shows rolling LLM spend
 * (24h / 7d / 30d), per-surface breakdown, and the rolling-mean preview
 * the operator can use to decide "is it worth running this drain right
 * now". Drill-down lists the 10 most-recent evaluations and corrections
 * with their per-correlation cost so an outlier ("which evaluation cost
 * 12 cents?") is one scroll away.
 *
 * Numbers are computed on the server from `labs_llm_costs` — every
 * Call 1/2/3/5 + every patch-proposer + every rescore writes a row
 * there. See `server/labs/llm-cost-recorder.ts` for the recorder
 * contract and `server/labs/llm-pricing.ts` for the per-model rates.
 */

interface CostRollup {
  totalUsd: number;
  callCount: number;
  promptTokens: number;
  completionTokens: number;
}

interface SurfaceRow {
  surface: string;
  totalUsd: number;
  callCount: number;
}

interface CorrelationRow {
  correlationId: string;
  surface: string;
  totalUsd: number;
  callCount: number;
}

interface LlmCostDashboard {
  totals: {
    last24h: CostRollup;
    last7d: CostRollup;
    last30d: CostRollup;
  };
  bySurface30d: SurfaceRow[];
  perEvaluationEstimate: { meanUsd: number; sampleSize: number };
  perCorrectionEstimate: { meanUsd: number; sampleSize: number };
  recentEvaluations: CorrelationRow[];
  recentCorrections: CorrelationRow[];
}

function formatUsd(n: number): string {
  if (n === 0) return "$0";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

const SURFACE_LABELS: Record<string, string> = {
  evaluation: "Avaliações de perfil",
  calibration: "Calibrações (drain)",
  rerun: "Rerun de correções",
  baseline: "Baseline",
};

function surfaceLabel(slug: string): string {
  return SURFACE_LABELS[slug] ?? slug;
}

export function LlmCostsSection() {
  const { data, isLoading, error } = useQuery<LlmCostDashboard>({
    queryKey: ["labs", "calibrationRunsLlmCosts"],
    queryFn: () => rpcClient.labs.calibrationRunsLlmCosts(),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-6 text-center">Carregando custos…</div>;
  }
  if (error || !data) {
    return (
      <div className="text-sm text-rose-700 py-6 text-center">
        Falha ao carregar custos LLM. {error instanceof Error ? error.message : ""}
      </div>
    );
  }

  return (
    <div className="llm-costs space-y-6">
      {/* Rolling totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <CostTile label="Últimas 24h" rollup={data.totals.last24h} />
        <CostTile label="Últimos 7 dias" rollup={data.totals.last7d} />
        <CostTile label="Últimos 30 dias" rollup={data.totals.last30d} />
      </div>

      {/* Per-call mean: the "preview" surface */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <EstimateTile
          label="Por avaliação de perfil"
          subLabel="média móvel · 30d"
          estimate={data.perEvaluationEstimate}
        />
        <EstimateTile
          label="Por correção (calibração)"
          subLabel="média móvel · 30d"
          estimate={data.perCorrectionEstimate}
        />
      </div>

      {/* Per-surface breakdown */}
      {data.bySurface30d.length > 0 && (
        <section className="llm-costs-block">
          <header>
            <strong>Onde o gasto foi parar</strong>
            <span className="text-muted-foreground text-xs">últimos 30 dias</span>
          </header>
          <ul className="llm-costs-surface-list">
            {data.bySurface30d.map((s) => (
              <li key={s.surface} className="llm-costs-surface-row">
                <span className="llm-costs-surface-label">{surfaceLabel(s.surface)}</span>
                <span className="llm-costs-surface-bar" aria-hidden>
                  <span
                    className="llm-costs-surface-fill"
                    style={{
                      width: `${
                        (s.totalUsd / Math.max(0.0001, data.totals.last30d.totalUsd)) * 100
                      }%`,
                    }}
                  />
                </span>
                <span className="tabular-nums text-sm font-medium">{formatUsd(s.totalUsd)}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {s.callCount} chamada{s.callCount === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Recent evaluations drill-down */}
      {data.recentEvaluations.length > 0 && (
        <section className="llm-costs-block">
          <header>
            <strong>Últimas avaliações de perfil</strong>
            <span className="text-muted-foreground text-xs">
              {data.recentEvaluations.length} mostradas · ordenadas por mais recentes
            </span>
          </header>
          <CorrelationList rows={data.recentEvaluations} />
        </section>
      )}

      {/* Recent corrections drill-down */}
      {data.recentCorrections.length > 0 && (
        <section className="llm-costs-block">
          <header>
            <strong>Últimas correções (calibração)</strong>
            <span className="text-muted-foreground text-xs">
              {data.recentCorrections.length} mostradas · ordenadas por mais recentes
            </span>
          </header>
          <CorrelationList rows={data.recentCorrections} />
        </section>
      )}

      {data.totals.last30d.callCount === 0 && (
        <div className="llm-costs-empty">
          <strong>Nada registrado ainda.</strong> A partir das próximas avaliações e calibrações,
          cada chamada de LLM grava uma linha em <code>labs_llm_costs</code> com o uso de tokens +
          custo computado. Volte aqui depois da próxima rodada.
        </div>
      )}
    </div>
  );
}

function CostTile({ label, rollup }: { label: string; rollup: CostRollup }) {
  return (
    <div className="llm-costs-tile">
      <div className="llm-costs-tile-head">
        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <div className="llm-costs-tile-value tabular-nums">{formatUsd(rollup.totalUsd)}</div>
      <div className="llm-costs-tile-meta text-xs text-muted-foreground">
        {rollup.callCount} chamada{rollup.callCount === 1 ? "" : "s"} ·{" "}
        {formatTokens(rollup.promptTokens + rollup.completionTokens)} tokens
      </div>
    </div>
  );
}

function EstimateTile({
  label,
  subLabel,
  estimate,
}: {
  label: string;
  subLabel: string;
  estimate: { meanUsd: number; sampleSize: number };
}) {
  const empty = estimate.sampleSize === 0;
  return (
    <div className="llm-costs-tile">
      <div className="llm-costs-tile-head">
        <Zap className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <div className="llm-costs-tile-value tabular-nums">
        {empty ? "—" : formatUsd(estimate.meanUsd)}
      </div>
      <div className="llm-costs-tile-meta text-xs text-muted-foreground">
        {empty
          ? "sem amostras ainda"
          : `${subLabel} · ${estimate.sampleSize} amostra${estimate.sampleSize === 1 ? "" : "s"}`}
      </div>
    </div>
  );
}

function CorrelationList({ rows }: { rows: CorrelationRow[] }) {
  return (
    <ul className="llm-costs-correlation-list">
      {rows.map((r) => (
        <li key={`${r.surface}-${r.correlationId}`} className="llm-costs-correlation-row">
          <code className="font-mono text-[11px] text-muted-foreground">
            {r.correlationId.slice(0, 8)}…
          </code>
          <span className="text-xs text-muted-foreground">
            {r.callCount} chamada{r.callCount === 1 ? "" : "s"}
          </span>
          <span className="tabular-nums font-medium ml-auto">{formatUsd(r.totalUsd)}</span>
        </li>
      ))}
    </ul>
  );
}
